import { useEffect } from 'react';
import Header from '../../../components/base/Header';
import Footer from '../../../components/base/Footer';
import { Link } from 'react-router-dom';

export default function PlantCommunicationBlog() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-gradient-to-br from-green-50 via-emerald-50 to-teal-50">
      <Header />

      {/* Hero Section */}
      <section className="relative py-16 bg-gradient-to-r from-emerald-600 to-teal-600">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <div className="inline-block mb-6">
              <div className="w-20 h-20 bg-white/20 rounded-full flex items-center justify-center backdrop-blur-sm">
                <i className="ri-chat-3-line text-5xl text-white"></i>
              </div>
            </div>
            <h1 className="text-4xl md:text-5xl font-bold text-white mb-4">
              植物は本当に「会話」しているのか？
            </h1>
            <p className="text-xl text-white/90 mb-6">
              ── 沈黙の中で交わされる、生物学的コミュニケーション ──
            </p>
            <div className="flex items-center justify-center gap-4 text-white/90">
              <span className="flex items-center">
                <i className="ri-calendar-line mr-2"></i>
                2026年12月15日
              </span>
              <span className="flex items-center">
                <i className="ri-user-line mr-2"></i>
                ももちゃん
              </span>
            </div>
          </div>
        </div>
      </section>

      {/* Featured Image */}
      <section className="py-8 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div className="rounded-3xl overflow-hidden shadow-2xl">
              <img
                src="https://public.readdy.ai/ai/img_res/78b9062d431a9db95d90653c4d5c267d.jpg"
                alt="植物のコミュニケーション"
                className="w-full h-auto object-cover"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Article Content */}
      <article className="py-12 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto">
            {/* Conclusion */}
            <div className="mb-12 bg-gradient-to-br from-emerald-50 to-teal-50 rounded-3xl p-8 border-l-4 border-emerald-500">
              <h2 className="text-3xl font-bold text-emerald-800 mb-6 flex items-center">
                <span className="text-4xl mr-3">📊</span>
                論文的結論
              </h2>
              <div className="bg-white rounded-2xl p-8 text-center border-2 border-emerald-200">
                <p className="text-3xl font-bold text-emerald-800 mb-4">
                  👉 YES（ただし、言語ではない）
                </p>
              </div>
              <div className="mt-6 space-y-4 text-gray-700 leading-relaxed">
                <p>
                  植物は言葉を使いません。<br />
                  声を出すことも、身振り手振りで意思を示すこともありません。
                </p>
                <p className="font-medium text-emerald-800">
                  それでも、生物学の視点で見たとき、<br />
                  植物は確かに「コミュニケーションしている存在」だと言えます。
                </p>
                <p>
                  この主張は比喩でも、ロマンでもありません。<br />
                  実験と論文によって、繰り返し示されてきた事実です。
                </p>
              </div>
            </div>

            {/* Section 1: Definition */}
            <div className="mb-12 bg-gradient-to-br from-teal-50 to-cyan-50 rounded-3xl p-8">
              <h2 className="text-3xl font-bold text-teal-800 mb-6 flex items-center">
                <span className="text-4xl mr-3">💬</span>
                会話（コミュニケーション）の定義とは何か
              </h2>
              <div className="space-y-4 text-gray-700 leading-relaxed">
                <p>
                  私たちはつい、<br />
                  <strong>会話＝言葉のやり取り</strong><br />
                  だと考えてしまいます。
                </p>
                <p className="font-medium text-teal-800">
                  しかし生物学において、<br />
                  コミュニケーションの定義はもっとシンプルです。
                </p>
                <div className="bg-white rounded-2xl p-8 my-6 border-l-4 border-teal-400">
                  <p className="text-lg font-bold text-teal-800 mb-4">
                    以下の3点が成立していれば、<br />
                    それは「会話」と呼ぶことができます。
                  </p>
                  <div className="space-y-4 mt-6">
                    <div className="flex items-start">
                      <div className="w-10 h-10 bg-teal-100 rounded-full flex items-center justify-center flex-shrink-0 mr-4">
                        <span className="text-teal-700 font-bold">1</span>
                      </div>
                      <div>
                        <h3 className="text-lg font-bold text-teal-700 mb-1">情報を発する</h3>
                      </div>
                    </div>
                    <div className="flex items-start">
                      <div className="w-10 h-10 bg-teal-100 rounded-full flex items-center justify-center flex-shrink-0 mr-4">
                        <span className="text-teal-700 font-bold">2</span>
                      </div>
                      <div>
                        <h3 className="text-lg font-bold text-teal-700 mb-1">情報を受け取る</h3>
                      </div>
                    </div>
                    <div className="flex items-start">
                      <div className="w-10 h-10 bg-teal-100 rounded-full flex items-center justify-center flex-shrink-0 mr-4">
                        <span className="text-teal-700 font-bold">3</span>
                      </div>
                      <div>
                        <h3 className="text-lg font-bold text-teal-700 mb-1">それに応じて行動（反応）を変える</h3>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="bg-gradient-to-r from-teal-100 to-emerald-100 rounded-2xl p-6 my-6">
                  <p className="text-teal-800 font-medium text-lg text-center">
                    驚くべきことに、<br />
                    植物はこのすべてを行っています。
                  </p>
                </div>
              </div>
            </div>

            {/* Section 2: Information Exchange */}
            <div className="mb-12 bg-gradient-to-br from-green-50 to-emerald-50 rounded-3xl p-8">
              <h2 className="text-3xl font-bold text-green-800 mb-6 flex items-center">
                <span className="text-4xl mr-3">🌿</span>
                植物が行っている「情報のやり取り」
              </h2>
              <div className="space-y-4 text-gray-700 leading-relaxed">
                <p>
                  植物は、じっと動かず、黙っているように見えます。<br />
                  しかしその内部と周囲では、<br />
                  常に情報のやり取りが行われています。
                </p>
                
                <div className="grid md:grid-cols-2 gap-4 my-6">
                  <div className="bg-white rounded-2xl p-6 shadow-md border-l-4 border-green-400">
                    <h3 className="text-lg font-bold text-green-700 mb-2 flex items-center">
                      <i className="ri-mist-line mr-2"></i>
                      匂い（揮発性化学物質）を空気中に放つ
                    </h3>
                  </div>
                  <div className="bg-white rounded-2xl p-6 shadow-md border-l-4 border-emerald-400">
                    <h3 className="text-lg font-bold text-emerald-700 mb-2 flex items-center">
                      <i className="ri-git-branch-line mr-2"></i>
                      地下で菌根菌ネットワークを介してつながる
                    </h3>
                  </div>
                  <div className="bg-white rounded-2xl p-6 shadow-md border-l-4 border-teal-400">
                    <h3 className="text-lg font-bold text-teal-700 mb-2 flex items-center">
                      <i className="ri-flashlight-line mr-2"></i>
                      体内で電気信号やカルシウム波を走らせる
                    </h3>
                  </div>
                  <div className="bg-white rounded-2xl p-6 shadow-md border-l-4 border-cyan-400">
                    <h3 className="text-lg font-bold text-cyan-700 mb-2 flex items-center">
                      <i className="ri-bug-line mr-2"></i>
                      昆虫や周囲の植物の存在に応じて反応を変える
                    </h3>
                  </div>
                </div>

                <div className="bg-white rounded-2xl p-8 my-6 border-2 border-green-200">
                  <p className="text-green-800 font-medium text-lg mb-4">
                    これらは、<br />
                    「そう見える」という話ではありません。
                  </p>
                  <p className="text-green-800 font-bold text-xl">
                    すべて、<br />
                    実験によって確認された現象です。
                  </p>
                </div>

                <p className="font-medium text-green-800 text-lg">
                  植物は、<br />
                  外界を感知し、<br />
                  情報を解釈し、<br />
                  行動を変えています。
                </p>
              </div>
            </div>

            {/* Section 3: Research Papers */}
            <div className="mb-12 bg-gradient-to-br from-cyan-50 to-blue-50 rounded-3xl p-8">
              <h2 className="text-3xl font-bold text-cyan-800 mb-6 flex items-center">
                <span className="text-4xl mr-3">📚</span>
                代表的な根拠論文
              </h2>
              <div className="space-y-4 text-gray-700 leading-relaxed">
                <p>
                  この考えを支えているのが、<br />
                  以下のような研究です。
                </p>
                
                <div className="space-y-4 my-6">
                  <div className="bg-white rounded-2xl p-6 shadow-md border-l-4 border-cyan-400">
                    <p className="font-bold text-cyan-800 mb-2">
                      Heil & Karban, Trends in Ecology & Evolution, 2010
                    </p>
                  </div>
                  <div className="bg-white rounded-2xl p-6 shadow-md border-l-4 border-blue-400">
                    <p className="font-bold text-blue-800 mb-2">
                      Baldwin et al., Science, 2006
                    </p>
                  </div>
                </div>

                <div className="bg-white rounded-2xl p-8 my-6 border-2 border-cyan-200">
                  <p className="text-cyan-800 font-medium text-lg mb-4">
                    これらの論文は共通して、<br />
                    植物が
                  </p>
                  <ul className="space-y-2 text-cyan-700">
                    <li className="flex items-start">
                      <i className="ri-checkbox-circle-fill mr-2 mt-1 text-cyan-500"></i>
                      <span>環境から情報を感知し</span>
                    </li>
                    <li className="flex items-start">
                      <i className="ri-checkbox-circle-fill mr-2 mt-1 text-cyan-500"></i>
                      <span>他の生物の存在を読み取り</span>
                    </li>
                    <li className="flex items-start">
                      <i className="ri-checkbox-circle-fill mr-2 mt-1 text-cyan-500"></i>
                      <span>それに応じて生理反応を変化させる</span>
                    </li>
                  </ul>
                  <p className="text-cyan-800 font-medium text-lg mt-4">
                    能力を持つことを示しています。
                  </p>
                </div>

                <p className="font-bold text-cyan-800 text-lg">
                  つまり植物は、<br />
                  外界を「感じていない存在」ではありません。
                </p>
              </div>
            </div>

            {/* Section 4: Sensing and Responding */}
            <div className="mb-12 bg-gradient-to-br from-emerald-50 to-green-50 rounded-3xl p-8">
              <h2 className="text-3xl font-bold text-emerald-800 mb-6 flex items-center">
                <span className="text-4xl mr-3">🌱</span>
                感じ、受け取り、応答している存在
              </h2>
              <div className="space-y-4 text-gray-700 leading-relaxed">
                <p className="text-lg">
                  植物は、
                </p>
                
                <div className="bg-white rounded-2xl p-8 my-6 border-l-4 border-emerald-400">
                  <ul className="space-y-3 text-emerald-700 text-lg">
                    <li className="flex items-start">
                      <i className="ri-eye-line mr-3 mt-1 text-emerald-500 text-xl"></i>
                      <span>何が起きているかを察知し</span>
                    </li>
                    <li className="flex items-start">
                      <i className="ri-compass-line mr-3 mt-1 text-emerald-500 text-xl"></i>
                      <span>どこからの刺激かを区別し</span>
                    </li>
                    <li className="flex items-start">
                      <i className="ri-lightbulb-line mr-3 mt-1 text-emerald-500 text-xl"></i>
                      <span>どう対応するかを選択する</span>
                    </li>
                  </ul>
                </div>

                <p className="font-medium text-emerald-800 text-lg">
                  そうした判断を、<br />
                  神経も脳も持たずに行っています。
                </p>

                <div className="bg-gradient-to-r from-emerald-100 to-green-100 rounded-2xl p-6 my-6">
                  <p className="text-emerald-800 font-medium text-lg">
                    声を上げることも、<br />
                    動いて逃げることもできない代わりに、<br />
                    植物は情報のやり取りを極限まで洗練させたのです。
                  </p>
                </div>
              </div>
            </div>

            {/* Section 5: Silence is Not Nothing */}
            <div className="mb-12 bg-gradient-to-br from-teal-50 to-emerald-50 rounded-3xl p-8">
              <h2 className="text-3xl font-bold text-teal-800 mb-6 flex items-center">
                <span className="text-4xl mr-3">🤫</span>
                沈黙は「無」ではない
              </h2>
              <div className="space-y-4 text-gray-700 leading-relaxed">
                <p>
                  私たちは、<br />
                  沈黙を「何も起きていない状態」だと<br />
                  思いがちです。
                </p>
                
                <div className="bg-white rounded-2xl p-8 my-6 text-center border-2 border-teal-200">
                  <p className="text-teal-800 font-medium text-lg mb-4">
                    しかし植物の沈黙は、<br />
                    無ではありません。
                  </p>
                  <p className="text-teal-800 font-bold text-xl">
                    それは、
                  </p>
                </div>

                <div className="bg-gradient-to-r from-teal-100 to-emerald-100 rounded-2xl p-8 my-6">
                  <ul className="space-y-3 text-teal-800 text-lg">
                    <li className="flex items-start">
                      <i className="ri-radio-line mr-3 mt-1 text-teal-600 text-xl"></i>
                      <span>情報が飛び交い、</span>
                    </li>
                    <li className="flex items-start">
                      <i className="ri-refresh-line mr-3 mt-1 text-teal-600 text-xl"></i>
                      <span>応答が積み重なり、</span>
                    </li>
                    <li className="flex items-start">
                      <i className="ri-shield-check-line mr-3 mt-1 text-teal-600 text-xl"></i>
                      <span>生存戦略が更新され続けている状態</span>
                    </li>
                  </ul>
                  <p className="text-teal-800 text-lg mt-4">
                    です。
                  </p>
                </div>

                <div className="bg-white rounded-2xl p-8 my-6 text-center border-2 border-emerald-200">
                  <p className="text-2xl font-bold text-emerald-800 mb-4">
                    その意味で、<br />
                    植物は今この瞬間も、<br />
                    沈黙のまま会話を続けていると言えるのです。
                  </p>
                </div>
              </div>
            </div>

            {/* Closing Message */}
            <div className="mb-12 bg-gradient-to-br from-green-50 to-emerald-50 rounded-3xl p-8 text-center">
              <h2 className="text-3xl font-bold text-green-800 mb-6">
                🌿 植物の世界は、まだまだ奥深い
              </h2>
              <p className="text-gray-700 leading-relaxed mb-4">
                私たちが見ている植物の姿は、<br />
                ほんの一部に過ぎません。<br />
                その沈黙の中には、驚くべき世界が広がっています✨
              </p>
              <p className="text-lg font-bold text-green-800">
                これからも、植物の不思議を一緒に探求していきましょう😊
              </p>
            </div>

            {/* Tags */}
            <div className="flex flex-wrap gap-2 mb-12">
              <span className="px-4 py-2 bg-emerald-100 text-emerald-700 text-sm rounded-full">
                #植物科学
              </span>
              <span className="px-4 py-2 bg-emerald-100 text-emerald-700 text-sm rounded-full">
                #コミュニケーション
              </span>
              <span className="px-4 py-2 bg-emerald-100 text-emerald-700 text-sm rounded-full">
                #生物学
              </span>
              <span className="px-4 py-2 bg-emerald-100 text-emerald-700 text-sm rounded-full">
                #植物の不思議
              </span>
              <span className="px-4 py-2 bg-emerald-100 text-emerald-700 text-sm rounded-full">
                #論文解説
              </span>
            </div>

            {/* Back Button */}
            <div className="text-center">
              <Link
                to="/essay"
                className="inline-flex items-center bg-gradient-to-r from-emerald-500 to-teal-500 hover:from-emerald-600 hover:to-teal-600 text-white font-bold py-4 px-8 rounded-full transition-all duration-300 transform hover:scale-105 shadow-lg hover:shadow-xl whitespace-nowrap"
              >
                <i className="ri-arrow-left-line mr-2"></i>
                ブログ一覧に戻る
              </Link>
            </div>
          </div>
        </div>
      </article>

      {/* Related Posts */}
      <section className="py-16 bg-gradient-to-br from-green-50 to-emerald-50">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl font-bold text-green-800 mb-8 text-center">
              他のブログ記事も読む
            </h2>
            <div className="text-center">
              <Link
                to="/essay"
                className="inline-flex items-center text-green-600 hover:text-green-700 font-medium transition-colors"
              >
                すべての記事を見る
                <i className="ri-arrow-right-line ml-2"></i>
              </Link>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
